import { useEffect } from 'react';

/**
 * Sign the user out after IDLE_MS of no interaction, bouncing back to login.
 *
 * Wall-clock based (not a single setTimeout): we stash the last-activity epoch in
 * localStorage and poll it. That survives reloads, is shared across tabs, and is
 * immune to background-tab timer throttling — a late interval tick still catches
 * the elapsed time, and we also re-check whenever the tab becomes visible.
 *
 * ponytail: 12h hard timeout, no pre-logout warning modal. Add a warning if UX
 * wants a grace prompt.
 */
const IDLE_MS = 12 * 60 * 60 * 1000; // 12 hours
const KEY = 'vh.auth.lastActivity';
const WRITE_THROTTLE_MS = 30_000;
const CHECK_INTERVAL_MS = 30_000;

export function useIdleLogout(active: boolean, onIdle: () => void): void {
  useEffect(() => {
    if (!active) return;

    let last = Date.now();
    localStorage.setItem(KEY, String(last));

    // Activity resets the clock; throttle the write so mousemove/scroll don't
    // hammer localStorage.
    const bump = () => {
      const now = Date.now();
      if (now - last > WRITE_THROTTLE_MS) {
        last = now;
        localStorage.setItem(KEY, String(now));
      }
    };
    const events = ['pointerdown', 'keydown', 'scroll', 'touchstart'] as const;
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));

    const check = () => {
      const t = Number(localStorage.getItem(KEY)) || Date.now();
      if (Date.now() - t >= IDLE_MS) onIdle();
    };
    const id = window.setInterval(check, CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', check);

    return () => {
      events.forEach((e) => window.removeEventListener(e, bump));
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', check);
    };
  }, [active, onIdle]);
}
